import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { nhomNghin } from '../../format';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  Typography,
} from 'antd';
import type { FormListFieldData } from 'antd/es/form';
import dayjs, { type Dayjs } from 'dayjs';
import { Eye, Palette, Plus, Trash, WarningCircle } from '@phosphor-icons/react';
import { DataTable } from '../../components/DataTable';
import { StatusTag } from '../../components/StatusTag';
import { HintBanner, HintToggle, useDismissibleHint } from '../../components/DismissibleHint';
import {
  CYCLE_LABELS,
  DOWNGRADE_LABELS,
  SEED_TIER_LEVELS,
  describeTierVersionState,
  type CreateTierDraftPayload,
  type TierCycle,
  type TierDefinition,
  type TierDowngradePolicy,
  type TierColor,
  type TierImpactReport,
  type TierLevel,
  type UpdateTierDraftPayload,
} from './api';
import { tokens } from '../../theme';
import { TierChip, TierColorPicker } from './TierColorPicker';
import { TierColorsModal } from './TierColorsModal';

/** Cũng dùng lại ở Modal xác nhận phát hành trong TiersPage.tsx. */
export function ImpactPreview({ report }: { report: TierImpactReport }) {
  return (
    <>
      <Typography.Paragraph style={{ marginBottom: 12 }}>{report.summary}</Typography.Paragraph>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <div style={{ fontSize: 12, color: tokens.color.ink3 }}>Thành viên có hạng</div>
          <div style={{ fontWeight: 600 }}>{report.membersWithTier.toLocaleString('vi-VN')}</div>
        </Col>
        <Col span={8}>
          <div style={{ fontSize: 12, color: tokens.color.ink3 }}>Lên hạng (GD tiếp theo)</div>
          <div style={{ fontWeight: 600, color: tokens.color.ok }}>
            +{report.upgradeOnNextTransaction.toLocaleString('vi-VN')}
          </div>
        </Col>
        <Col span={8}>
          <div style={{ fontSize: 12, color: tokens.color.ink3 }}>Xuống hạng (nếu chốt chu kỳ)</div>
          <div style={{ fontWeight: 600, color: tokens.color.warn }}>
            −{report.downgradeIfCycleEndedNow.toLocaleString('vi-VN')}
          </div>
        </Col>
      </Row>
      {report.membersMoved > 0 && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningCircle aria-hidden />}
          style={{ marginBottom: 12 }}
          message={`${report.membersMoved.toLocaleString('vi-VN')} thành viên bị chuyển hạng do mất mã hạng`}
        />
      )}
      <DataTable
        toolbar={false}
        rowKey="code"
        size="small"
        pagination={false}
        dataSource={report.byTier}
        columns={[
          { title: 'Mã hạng', dataIndex: 'code', width: 100 },
          { title: 'Tên', dataIndex: 'name', render: (v: string | null) => v ?? '—' },
          {
            title: 'Số thành viên',
            dataIndex: 'memberCount',
            width: 120,
            render: (n: number) => n.toLocaleString('vi-VN'),
          },
          {
            title: 'Trạng thái',
            key: 'status',
            render: (_, row) =>
              row.stillExists ? (
                <StatusTag color="success">Còn trong bản mới</StatusTag>
              ) : (
                <StatusTag color="warning">Chuyển → {row.movesTo ?? '?'}</StatusTag>
              ),
          },
        ]}
      />
    </>
  );
}

interface TierFormPanelProps {
  version: TierDefinition | null;
  isCreating: boolean;
  submitting: boolean;
  onSubmit: (values: CreateTierDraftPayload | UpdateTierDraftPayload) => void;
  // Hủy/Lưu bản nháp/Phát hành chuyển lên PageHeader ở trang cha (giống prototype), gọi thẳng
  // hàm của trang cha — panel chỉ còn lộ `submit()` qua ref (xem TierFormPanelHandle).
  onPreviewImpact: () => void;
  impactLoading: boolean;
  impactPreview: TierImpactReport | null;

  versions: TierDefinition[];
  /** Bảng màu hạng máy chủ trả về. Rỗng khi hệ thống chưa có bản thang hạng nào. */
  allowedColors: TierColor[];
  selectedId: string | null;
  onSelectVersion: (v: TierDefinition) => void;
  onCreateNew: () => void;
}

interface FormShape {
  version: string;
  cycle: TierCycle;
  downgradePolicy: TierDowngradePolicy;
  keepThresholdRatio: number;
  effectiveFrom: Dayjs;
  note?: string;
  levels: TierLevel[];
}

const defaultDraft = (): Partial<FormShape> => ({
  version: '',
  cycle: 'CalendarYear',
  downgradePolicy: 'OneLevel',
  keepThresholdRatio: 0.5,
  effectiveFrom: dayjs().add(1, 'day').startOf('day'),
  levels: SEED_TIER_LEVELS.map((l) => ({ ...l })),
});

function formatEffective(iso: string): string {
  return new Date(iso).toLocaleString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Form sửa/xem MỘT bản thang hạng, cộng sidebar lịch sử — bố cục 2 cột giống prototype
 * (`a-tier`, `g-2-1`), nằm thẳng trên trang thay vì trong Drawer trượt ra (đổi 2026-09-01,
 * cùng cách đã làm ở Quy tắc tích điểm — xem `RuleFormPanel.tsx`).
 *
 * <p>🔴 <b>Không đổi bất biến versioning</b> — bản đã phát hành vẫn khóa nội dung
 * (`readOnly`), sửa chỉ khả thi trên bản NHÁP. Chỉ đổi vị trí form và cách chọn bản cũ.</p>
 */
/** Cho trang cha (TiersPage) gọi "Lưu bản nháp" từ nút đặt ở PageHeader, giống prototype. */
export interface TierFormPanelHandle {
  submit: () => void;
}

export const TierFormPanel = forwardRef<TierFormPanelHandle, TierFormPanelProps>(function TierFormPanel({
  version,
  isCreating,
  submitting,
  onSubmit,
  onPreviewImpact,
  impactLoading,
  impactPreview,
  versions,
  allowedColors,
  selectedId,
  onSelectVersion,
  onCreateNew,
}, ref) {
  const [form] = Form.useForm<FormShape>();
  // Theo dõi sống để khóa màu hạng khác đang dùng và vẽ chip xem trước ngay khi gõ tên.
  const watchedLevels = Form.useWatch('levels', form) as TierLevel[] | undefined;

  const readOnly = version?.isPublished === true;
  const hasContent = isCreating || version !== null;
  // S4-BE-17: màu hạng đổi được ngay trên bản đã phát hành. Bản đã bị thay thế thì không còn ai
  // thấy màu của nó, nên không mở nút.
  const canRecolor =
    readOnly && version !== null && describeTierVersionState(version, versions).label !== 'Đã thay thế';
  const [recolorOpen, setRecolorOpen] = useState(false);
  const earningHint = useDismissibleHint('tier-form-earning-link');

  useEffect(() => {
    if (!hasContent) return;

    if (version) {
      form.setFieldsValue({
        version: version.version,
        cycle: version.cycle,
        downgradePolicy: version.downgradePolicy,
        keepThresholdRatio: version.keepThresholdRatio,
        effectiveFrom: dayjs(version.effectiveFrom),
        note: version.note ?? undefined,
        levels: version.levels.map((l) => ({ ...l })),
      });
    } else {
      form.setFieldsValue(defaultDraft() as FormShape);
    }
  }, [version, hasContent, form]);

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const body = {
          levels: values.levels,
          cycle: values.cycle,
          downgradePolicy: values.downgradePolicy,
          keepThresholdRatio: values.keepThresholdRatio,
          effectiveFrom: values.effectiveFrom.toISOString(),
          note: values.note?.trim() || null,
        };

        if (isCreating) {
          onSubmit({ version: values.version.trim(), ...body });
        } else {
          onSubmit(body);
        }
      })
      .catch(() => {});
  };

  useImperativeHandle(ref, () => ({ submit: handleSubmit }), [handleSubmit]);

  // Lưới các bậc hạng. Header và từng dòng PHẢI dùng chung một định nghĩa — trước đây chuỗi này
  // chép hai chỗ, sửa một chỗ là nhãn lệch khỏi ô mà không có gì báo.
  //
  // 🔴 `Mã` để rộng CỐ ĐỊNH thay vì `1fr`: thẻ này nằm ở cột trái ~320–600px, mà ba cột `1fr`
  // cạnh ba cột 90px thì phần còn lại chia ba, có lúc còn ~10px — ô co lại bằng một ký tự và
  // nhãn xuống dòng từng chữ. Cả bảng vẫn "hiện đủ", chỉ là không đọc được gì.
  const levelGridColumns = readOnly
    ? '108px 1fr 96px 104px 1fr 96px 124px'
    : '108px 1fr 96px 104px 1fr 96px 124px 40px';

  /** Màu mà các bậc KHÁC trong bản đang chọn → tên bậc đó. */
  const colorsTakenBy = (index: number) =>
    new Map(
      (watchedLevels ?? [])
        .filter((l, i) => i !== index && !!l?.colorHex)
        .map((l) => [l.colorHex!.toLowerCase(), l.name || l.code || 'khác'] as const),
    );

  const levelRow = (field: FormListFieldData, remove: (index: number) => void) => (
    <div
      key={field.key}
      style={{
        display: 'grid',
        gridTemplateColumns: levelGridColumns,
        gap: 8,
        marginBottom: 8,
        alignItems: 'start',
      }}
    >
      <Form.Item
        name={[field.name, 'code']}
        rules={[{ required: true, message: 'Bắt buộc' }]}
        style={{ marginBottom: 0 }}
      >
        <Input disabled={readOnly} placeholder="gold" />
      </Form.Item>
      <Form.Item
        name={[field.name, 'name']}
        rules={[{ required: true, message: 'Bắt buộc' }]}
        style={{ marginBottom: 0 }}
      >
        <Input disabled={readOnly} />
      </Form.Item>
      <Form.Item
        name={[field.name, 'threshold']}
        rules={[{ required: true, message: 'Bắt buộc' }]}
        style={{ marginBottom: 0 }}
      >
        <InputNumber disabled={readOnly} min={0} style={{ width: '100%' }} formatter={nhomNghin.formatter} parser={nhomNghin.parser} />
      </Form.Item>
      {/*
        BR-308. Trần 50 là hạn mức khuyến mại của Nghị định 81/2018 — cùng con số BR-408 đặt cho
        voucher. Máy chủ vẫn kiểm lại và còn kiểm thêm một điều kiện giao diện không thấy được:
        bậc cao không được giảm ít hơn bậc thấp.
      */}
      <Form.Item name={[field.name, 'discountPercent']} style={{ marginBottom: 0 }}>
        {/*
          `suffix`, KHÔNG `addonAfter`: AntD 5.29 đã deprecate `addonAfter` trên InputNumber và in
          cảnh báo ra console mỗi lần dựng ô. Bắt được lúc đọc console của bản chạy thật.
        */}
        <InputNumber disabled={readOnly} min={0} max={50} suffix="%" style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item name={[field.name, 'benefits']} style={{ marginBottom: 0 }}>
        <Input disabled={readOnly} placeholder="Quyền lợi" />
      </Form.Item>
      <Form.Item name={[field.name, 'queuePriority']} valuePropName="checked" style={{ marginBottom: 0 }}>
        <Switch disabled={readOnly} />
      </Form.Item>
      <div style={{ paddingTop: 5 }}>
        <TierChip
          name={watchedLevels?.[field.name]?.name ?? ''}
          colorHex={watchedLevels?.[field.name]?.colorHex ?? null}
        />
      </div>
      {!readOnly && (
        <Button
          type="text"
          danger
          icon={<Trash size={16} aria-hidden />}
          aria-label="Xóa bậc"
          disabled={(form.getFieldValue('levels') as TierLevel[])?.length <= 1}
          onClick={() => remove(field.name)}
        />
      )}
      {!readOnly && (
        <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: tokens.color.ink2, flex: 'none' }}>Màu hạng</span>
          {allowedColors.length > 0 ? (
            <Form.Item name={[field.name, 'colorHex']} style={{ marginBottom: 0 }}>
              <TierColorPicker
                colors={allowedColors}
                takenBy={colorsTakenBy(field.name)}
                levelName={watchedLevels?.[field.name]?.name ?? ''}
                disabled={submitting}
              />
            </Form.Item>
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Lưu bản nháp trước để chọn màu. Chưa chọn thì hệ thống tự gán màu theo thứ tự hạng.
            </Typography.Text>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Tiêu đề nổi bật "X hạng đang áp dụng"/"Phiên bản X" — giống prototype (a-tier: "4 hạng
          đang áp dụng"), trước đây số hiệu chỉ nằm lẫn trong 1 ô nhập, không nổi bật. */}
      {hasContent && (
        <div style={{ marginBottom: tokens.space.sectionGap }}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {version ? version.version : 'Bản nháp mới'}
            {version && (
              <StatusTag color={describeTierVersionState(version, versions).tone} style={{ marginLeft: 10, verticalAlign: 'middle' }}>
                {describeTierVersionState(version, versions).label}
              </StatusTag>
            )}
          </Typography.Title>
        </div>
      )}

      <Row gutter={[tokens.space.sectionGap, tokens.space.sectionGap]}>
      <Col xs={24} lg={14}>
        {!hasContent ? (
          <Card size="small">
            <Empty description="Chưa có thang hạng nào. Tạo bản nháp để bắt đầu.">
              <Button type="primary" icon={<Plus size={14} aria-hidden />} onClick={onCreateNew}>
                Tạo bản nháp
              </Button>
            </Empty>
          </Card>
        ) : (
          <Card
            size="small"
            title="Định nghĩa các hạng"
            extra={
              <Space size="small">
                {readOnly && (
                  <StatusTag color="default" icon={<Eye size={12} aria-hidden />}>
                    Đã phát hành — chỉ xem
                  </StatusTag>
                )}
                {canRecolor && (
                  <Button size="small" icon={<Palette size={14} aria-hidden />} onClick={() => setRecolorOpen(true)}>
                    Sửa màu
                  </Button>
                )}
                <HintToggle hint={earningHint} />
              </Space>
            }
          >
            <HintBanner
              hint={earningHint}
              message="Hệ số tích điểm theo hạng cấu hình ở màn Quy tắc tích điểm"
              description="Hệ số khớp theo cột Mã, không phải Tên hiển thị. Đổi tên hiển thị thì hệ số vẫn đúng; đổi mã thì hệ số mất tác dụng mà không có cảnh báo nào."
            />

            <Form
              form={form}
              layout="vertical"
              requiredMark={false}
              disabled={readOnly || submitting}
              // "Đã phát hành" không phải "không dùng được" — đây là thang hạng đang áp cho mọi
              // khách, thứ người ta vào đây để ĐỌC. Xem `.reski-readonly-form` trong `index.css`.
              className={readOnly ? 'reski-readonly-form' : undefined}
            >
              {isCreating && (
                <Form.Item
                  name="version"
                  label="Số hiệu phiên bản"
                  rules={[{ required: true, message: 'Chưa nhập số hiệu.' }]}
                >
                  <Input placeholder="2026.12" style={{ maxWidth: 220 }} />
                </Form.Item>
              )}

              <Form.List name="levels">
                {(fields, { add, remove }) => (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <Typography.Text strong>Các bậc hạng</Typography.Text>
                      {!readOnly && (
                        <Button
                          size="small"
                          icon={<Plus size={14} aria-hidden />}
                          onClick={() =>
                            add({
                              code: '',
                              name: '',
                              threshold: 0,
                              queuePriority: false,
                              discountPercent: 0,
                              benefits: null,
                              colorHex: null,
                            })
                          }
                        >
                          Thêm bậc
                        </Button>
                      )}
                    </div>
                    {/*
                      Bảng rộng thì CUỘN NGANG trong khung của chính nó, không bóp cột. Thẻ này
                      nằm ở cột trái nên trên màn hẹp nó chỉ còn ~320px — ép sáu cột vào đó là
                      cách chắc chắn để không đọc được cột nào. Cuộn thì mất một thao tác, bóp
                      thì mất nội dung.
                    */}
                    <div style={{ overflowX: 'auto' }}>
                      <div style={{ minWidth: 750 }}>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: levelGridColumns,
                            gap: 8,
                            marginBottom: 8,
                            fontSize: 12,
                            color: tokens.color.ink3,
                          }}
                        >
                          <span>Mã</span>
                          <span>Tên hiển thị</span>
                          <span>Ngưỡng</span>
                          <span>Giảm hóa đơn</span>
                          <span>Quyền lợi</span>
                          <span>Ưu tiên queue</span>
                          <span>Màu</span>
                          {!readOnly && <span />}
                        </div>
                        {fields.map((field) => levelRow(field, remove))}
                      </div>
                    </div>
                  </>
                )}
              </Form.List>
            </Form>
          </Card>
        )}
      </Col>

      <Col xs={24} lg={10}>
        {hasContent && (
          /* `component={false}` nên Form không dựng thẻ DOM nào — gắn class lên Card bao ngoài. */
          <Card
            size="small"
            title="Chu kỳ xét hạng"
            className={readOnly ? 'reski-readonly-form' : undefined}
            style={{ marginBottom: tokens.space.sectionGap }}
          >
            <Form form={form} layout="vertical" requiredMark={false} disabled={readOnly || submitting} component={false}>
              <Form.Item name="cycle" label="Chu kỳ xét hạng" rules={[{ required: true }]}>
                <Select options={Object.entries(CYCLE_LABELS).map(([value, label]) => ({ value, label }))} />
              </Form.Item>

              <Form.Item name="downgradePolicy" label="Khi hết chu kỳ không đủ điểm giữ hạng">
                <Select options={Object.entries(DOWNGRADE_LABELS).map(([value, label]) => ({ value, label }))} />
              </Form.Item>

              <Form.Item
                name="keepThresholdRatio"
                label="Tỷ lệ ngưỡng để giữ hạng (hạ cánh mềm)"
                rules={[{ required: true }]}
                tooltip="Ví dụ 0,5 = đạt 50% ngưỡng hạng hiện tại thì được giữ."
              >
                <InputNumber min={0} max={1} step={0.05} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                name="effectiveFrom"
                label="Có hiệu lực từ"
                rules={[{ required: true, message: 'Chọn ngày hiệu lực.' }]}
              >
                <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item name="note" label="Ghi chú nội bộ">
                <Input.TextArea rows={2} maxLength={500} />
              </Form.Item>
            </Form>

            {readOnly ? (
              <Typography.Paragraph type="secondary" style={{ fontSize: 12.5, marginBottom: 0 }}>
                Bản đã phát hành không sửa được. Tạo bản nháp mới nếu cần thay đổi.
              </Typography.Paragraph>
            ) : (
              // Hủy/Lưu bản nháp/Phát hành chuyển lên PageHeader (giống prototype a-tier) —
              // xem TierFormPanelHandle.submit và TiersPage.tsx. "Xem trước" ở lại đây vì
              // không nằm trong 3 nút đó và không có trong prototype (tính năng riêng của ta).
              version && (
                <Button icon={<Eye size={14} aria-hidden />} loading={impactLoading} onClick={onPreviewImpact}>
                  Xem trước ảnh hưởng
                </Button>
              )
            )}
          </Card>
        )}

        {impactPreview && (
          <Card size="small" title="Ảnh hưởng nếu phát hành" style={{ marginBottom: tokens.space.sectionGap }}>
            <ImpactPreview report={impactPreview} />
          </Card>
        )}

        <Card
          size="small"
          title="Lịch sử phiên bản"
          extra={
            <Button size="small" type="primary" icon={<Plus size={14} aria-hidden />} onClick={onCreateNew}>
              Bản mới
            </Button>
          }
        >
          {versions.length === 0 ? (
            <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
              Chưa có bản nào.
            </Typography.Text>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {versions.map((v) => {
                const state = describeTierVersionState(v, versions);
                const isSelected = v.id === selectedId;
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => onSelectVersion(v)}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      padding: '8px 6px',
                      border: 'none',
                      borderRadius: tokens.radius.control,
                      background: isSelected ? tokens.color.green50 : 'transparent',
                      cursor: 'pointer',
                      textAlign: 'left',
                      font: 'inherit',
                      width: '100%',
                    }}
                  >
                    <span
                      aria-hidden
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: tokens.radius.pill,
                        marginTop: 5,
                        flex: 'none',
                        background:
                          state.tone === 'success'
                            ? tokens.color.ok
                            : state.tone === 'warning'
                              ? tokens.color.warn
                              : tokens.color.ink3,
                      }}
                    />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{v.version}</span>{' '}
                      <StatusTag color={state.tone} style={{ marginLeft: 2 }}>
                        {state.label}
                      </StatusTag>
                      <div style={{ fontSize: 12, color: tokens.color.ink3 }}>
                        {v.levels.length} bậc · {formatEffective(v.effectiveFrom)}
                      </div>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </Card>
      </Col>
      </Row>
      {canRecolor && version && recolorOpen && (
        <TierColorsModal version={version} open={recolorOpen} onClose={() => setRecolorOpen(false)} />
      )}
    </>
  );
});
